import { computed, inject, Injectable, isDevMode, signal } from '@angular/core';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
// Import the GraphQL services and types from minotaure-lib
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { FileDocument } from '@lib/models/file-document.model';
import { DocumentViewerOpenOptions } from '@lib/services/document-viewer-panel.service';
import { DocumentsService } from '@lib/services/documents.service';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { LoadingService } from '@lib/services/loading.service';
import { QpiViewerPanelService } from '@lib/services/qpi-viewer-panel.service';
import { RecueilService } from '@lib/services/recueil.service';
import { getRawFileId } from '@lib/utils/app-utils';
import { QueryManagerService } from 'finzzle-angular-utils';
import { isNil } from 'lodash';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import {
  BlobFilesPermissionDs,
  CalculateScoreCapacitePerteGQL,
  CalculateScoreEsgGQL,
  CapaciteSupportPertesDetail,
  ConnaissanceExperienceInput,
  ConnaissanceExperienceReponses,
  DocumentTypeEnum,
  EtatQpi,
  FileMetadata,
  GedDocumentType,
  GenerateQpiGQL,
  GetConnaissanceExperienceReferentielGQL,
  GetQpiResultsByInvestisseurIdGQL,
  GetToleranceRisqueReferentielGQL,
  InitInvestisseurQpiGQL,
  Investisseur,
  InvestisseurQpi,
  ProduitEpargneEnum,
  QpiCritereInput,
  QpiFormInput,
  QpiStateTransitionTrigger,
  RestartInvestisseurQpiGQL,
  SynchroniserSelectionAutoRecueilGQL,
  SaveConnaissanceExperienceGQL,
  SaveToleranceRisqueGQL,
  SignatureState,
  StorageFile,
  ToleranceRisqueInput,
  ToleranceRisqueReponses,
} from 'projects/minotaure-lib/src/public-api';

import { QpiCspUpdateDialogComponent, QpiCspUpdateDialogData } from '../qpi-csp-update-dialog/qpi-csp-update-dialog.component';
import {
  QpiSelectionAutoUpdateDialogComponent,
  QpiSelectionAutoUpdateDialogData,
} from '../qpi-selection-auto-update-dialog/qpi-selection-auto-update-dialog.component';
import { ConnaissanceExperienceReferentiel } from '../connaissance-experience/models/connaissance-experience.model';
import { ToleranceRisqueReferentiel } from '../tolerance-risque/models/tolerance-risque.model';

export interface ConnaissanceExperienceReferentielData {
  referentiel: ConnaissanceExperienceReferentiel;
  produitsEpargneDetenusRecueil: ProduitEpargneEnum[];
}
export interface QPIFormData {
  investisseurId: number;
  criteres: QpiCritereInput[];
  isComplete: boolean;
}
export interface QPIResults {
  id: number;
  resultatConnaissance?: number;
  resultatProfilDeRisque?: number;
  resultatToleranceAuRisque?: number;
  resultatCapacitePerte?: number;
  resultatSensibiliteExtraFinanciere?: number;
  versionProfil: number;
  /** Profil dans la version courante du questionnaire (seule version qui peut être complétée) */
  isVersionProfilCourante: boolean;
  /** Profil investisseur d'une version précédente du questionnaire, à recommencer dans la version courante */
  versionProfilObsolete: boolean;
  reponsesConnaissanceExperience?: ConnaissanceExperienceReponses | null;
  reponsesToleranceRisque?: ToleranceRisqueReponses | null;
  reponsesSensibiliteExtraFinanciereJson?: Array<QpiCritereInput>;
  capaciteSupportPertesDetail?: CapaciteSupportPertesDetail;
  investisseurId: number;
  isImportedFromAriane: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class QPIService {
  private readonly getQpiResultsGQL = inject(GetQpiResultsByInvestisseurIdGQL);
  private readonly initQpiGQL = inject(InitInvestisseurQpiGQL);
  private readonly calculateScoreCapacitePerteGQL = inject(CalculateScoreCapacitePerteGQL);
  private readonly calculateScoreEsgGQL = inject(CalculateScoreEsgGQL);
  private readonly getConnaissanceExperienceReferentielGQL = inject(GetConnaissanceExperienceReferentielGQL);
  private readonly saveConnaissanceExperienceGQL = inject(SaveConnaissanceExperienceGQL);
  private readonly getToleranceRisqueReferentielGQL = inject(GetToleranceRisqueReferentielGQL);
  private readonly saveToleranceRisqueGQL = inject(SaveToleranceRisqueGQL);
  private readonly GenerateQpiGQL = inject(GenerateQpiGQL);
  private readonly restartInvestisseurQpiGQL = inject(RestartInvestisseurQpiGQL);
  private readonly synchroniserSelectionAutoRecueilGQL = inject(SynchroniserSelectionAutoRecueilGQL);
  private readonly router = inject(Router);
  private readonly qpiViewerPanelService = inject(QpiViewerPanelService);
  private readonly documentService = inject(DocumentsService);
  private readonly investisseurService = inject(InvestisseurService);
  private readonly queryManagerService = inject(QueryManagerService);
  private readonly loadingService = inject(LoadingService);
  private readonly snackbarService = inject(OuiSnackbarService);
  private readonly dialogService = inject(OuiDialogService);
  private readonly datePipe = inject(DatePipe);
  private readonly recueilService = inject(RecueilService);
  /** Le profil de l'investisseur courant a été réalisé dans une version précédente du questionnaire : son document ne peut plus être généré */
  private readonly versionProfilObsolete = signal<boolean>(false);
  public readonly canGenerateQPI = computed(() => !this.versionProfilObsolete());
  private readonly qpiDocumentDescription = 'Profil Investisseur';
  private readonly investisseur = this.investisseurService.investisseurSig;
  /**
   * Retrieve all QPI results for an investor
   */
  public async getQPIResults(investisseurId: number): Promise<QPIResults | null> {
    const response = await lastValueFrom(
      this.getQpiResultsGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { investisseurId },
      })
    );
    if (response.error) {
      throw response.error;
    }
    const qpiData = response.data?.qpiResultsByInvestisseurId;
    const results = this.mapQpiResults(qpiData as InvestisseurQpi);
    if (investisseurId === this.investisseur()?.id) {
      this.versionProfilObsolete.set(results?.versionProfilObsolete ?? false);
    }
    return results;
  }
  public async initQpi(investisseurId: number): Promise<QPIResults> {
    const response = await lastValueFrom(
      this.initQpiGQL.mutate({
        variables: { investisseurId: investisseurId },
      })
    );
    if (response.error) {
      throw response.error;
    }
    const qpiData = response.data?.initInvestisseurQpi;
    return this.mapQpiResults(qpiData as InvestisseurQpi)!;
  }
  private mapQpiResults(dto?: InvestisseurQpi): QPIResults | null {
    if (isNil(dto)) return null;
    return {
      id: dto.id,
      resultatConnaissance: dto.resultatConnaissance ?? undefined,
      resultatProfilDeRisque: dto.resultatProfilDeRisque ?? undefined,
      resultatToleranceAuRisque: dto.resultatToleranceAuRisque ?? undefined,
      resultatCapacitePerte: dto.resultatCapacitePerte ?? undefined,
      resultatSensibiliteExtraFinanciere: dto.resultatSensibiliteExtraFinanciere ?? undefined,
      versionProfil: dto.versionProfil,
      isVersionProfilCourante: dto.isVersionProfilCourante,
      versionProfilObsolete: dto.versionProfilObsolete,
      reponsesConnaissanceExperience: dto.reponsesProfil?.connaissanceExperience ?? null,
      reponsesToleranceRisque: dto.reponsesProfil?.toleranceRisque ?? null,
      reponsesSensibiliteExtraFinanciereJson: this.parseQpiCriteres(dto.reponsesSensibiliteExtraFinanciereJson) ?? [],
      capaciteSupportPertesDetail: (dto.capaciteSupportPertesDetail as CapaciteSupportPertesDetail) ?? undefined,
      investisseurId: dto.investisseurId,
      isImportedFromAriane: dto.isImportedFromAriane,
    };
  }
  async calculateCsp(investisseurId: number): Promise<CapaciteSupportPertesDetail> {
    const result = await lastValueFrom(
      this.calculateScoreCapacitePerteGQL.mutate({
        variables: { investisseurPrincipalId: investisseurId },
      })
    );
    if (result.error) {
      throw result.error;
    }
    return result.data!.calculateScoreCapacitePerte!;
  }
  /**
   * Applique la sélection automatique des produits d'épargne d'après le recueil (questionnaire Connaissance et expérience)
   * et prévient le consultant si de nouveaux produits ont été sélectionnés.
   * @returns true si le profil a été modifié
   */
  async synchroniserSelectionAutoEtNotifier(investisseurId: number): Promise<boolean> {
    const result = await lastValueFrom(
      this.synchroniserSelectionAutoRecueilGQL.mutate({
        variables: { investisseurId },
      })
    );
    if (result.error) {
      throw result.error;
    }

    const synchronisation = result.data?.synchroniserSelectionAutoRecueil;
    if (!synchronisation?.produitsAjoutes.length) {
      return false;
    }

    await this.investisseurService.refreshRecueilState();

    const dialogData: QpiSelectionAutoUpdateDialogData = {
      produitsAjoutes: synchronisation.produitsAjoutes,
      isComplete: synchronisation.isComplete,
    };
    const dialogRef = this.dialogService.openDialog(QpiSelectionAutoUpdateDialogComponent, dialogData);
    if ((await firstValueFrom(dialogRef.afterClosed())) === true) {
      const code = this.investisseur()?.code;
      if (code) {
        // Ferme une éventuelle boîte de dialogue appelante (ex. envoi en signature) avant d'aller compléter le questionnaire
        this.dialogService.closeAll();
        this.router.navigate(['/investisseurs', 'from-list', code, 'connaissance-client', 'profil-investisseur'], {
          fragment: 'connaissances-experiences',
        });
      }
    }

    return true;
  }

  async recalculateCspAndNotifyChanges(investisseurId: number, currentScore?: number): Promise<boolean> {
    const newCsp = await this.calculateCsp(investisseurId);
    if (newCsp.score !== currentScore) {
      const dialogData: QpiCspUpdateDialogData = {
        isValid: newCsp.isValid,
        missingFields: newCsp.missingFields,
      };
      this.dialogService.openDialog(QpiCspUpdateDialogComponent, dialogData);
      const avancementConnaissanceClient = this.investisseur()?.avancementConnaissanceClientInvestisseur;
      if (avancementConnaissanceClient?.qpiEtat === EtatQpi.Signe && avancementConnaissanceClient.qpIdocumentID) {
        await this.investisseurService.updateQPIState(
          QpiStateTransitionTrigger.QpiFormHasBeenUncompleted,
          avancementConnaissanceClient.qpIdocumentID,
          investisseurId
        );
        if (newCsp.isValid) {
          await this.investisseurService.updateQPIState(
            QpiStateTransitionTrigger.QpiFormHasBeenCompleted,
            avancementConnaissanceClient.qpIdocumentID,
            investisseurId
          );
        }
      }
      await this.investisseurService.refreshRecueilState();
      return true;
    }
    return false;
  }
  public async calculateESGScore(formData: QPIFormData): Promise<QPIResults | null> {
    const formInput: QpiFormInput = {
      investisseurId: formData.investisseurId,
      criteres: formData.criteres.map(
        c =>
          ({
            id: c.id,
            valeur: c.valeur,
          }) as QpiCritereInput
      ),
      isComplete: formData.isComplete,
    };
    const result = await lastValueFrom(
      this.calculateScoreEsgGQL.mutate({
        variables: { formInput },
      })
    );
    const qpiData = result.data?.calculateScoreESG;
    if (result.error) {
      throw result.error;
    }
    return qpiData
      ? ({
          id: qpiData.id,
          resultatSensibiliteExtraFinanciere: qpiData.resultatSensibiliteExtraFinanciere ?? undefined,
          reponsesSensibiliteExtraFinanciereJson: this.parseQpiCriteres(qpiData.reponsesSensibiliteExtraFinanciereJson),
          investisseurId: qpiData.investisseurId,
        } as QPIResults)
      : null;
  }
  /**
   * Référentiel du questionnaire Connaissance et expérience et produits détenus d'après le recueil
   */
  public async getConnaissanceExperienceReferentiel(investisseurId: number): Promise<ConnaissanceExperienceReferentielData> {
    const response = await lastValueFrom(
      this.getConnaissanceExperienceReferentielGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { investisseurId },
      })
    );
    if (response.error) {
      throw response.error;
    }
    return {
      referentiel: response.data!.connaissanceExperienceReferentiel,
      produitsEpargneDetenusRecueil: response.data!.produitsEpargneDetenusRecueil,
    };
  }

  /**
   * Enregistre les réponses du questionnaire Connaissance et expérience (le back calcule le résultat)
   */
  public async saveConnaissanceExperience(input: ConnaissanceExperienceInput): Promise<QPIResults | null> {
    const result = await lastValueFrom(
      this.saveConnaissanceExperienceGQL.mutate({
        variables: { input },
      })
    );
    if (result.error) {
      throw result.error;
    }
    const qpiData = result.data?.saveConnaissanceExperience;
    return qpiData
      ? ({
          id: qpiData.id,
          investisseurId: qpiData.investisseurId,
          versionProfil: qpiData.versionProfil,
          resultatConnaissance: qpiData.resultatConnaissance ?? undefined,
          reponsesConnaissanceExperience: qpiData.reponsesProfil?.connaissanceExperience ?? null,
        } as QPIResults)
      : null;
  }

  /**
   * Référentiel du questionnaire Tolérance au risque (questions, réponses et scénarios, sans barème)
   */
  public async getToleranceRisqueReferentiel(): Promise<ToleranceRisqueReferentiel> {
    const response = await lastValueFrom(this.getToleranceRisqueReferentielGQL.fetch());
    if (response.error) {
      throw response.error;
    }
    return response.data!.toleranceRisqueReferentiel;
  }

  /**
   * Enregistre les réponses du questionnaire Tolérance au risque (le back calcule le résultat et le profil de risque)
   */
  public async saveToleranceRisque(input: ToleranceRisqueInput): Promise<QPIResults | null> {
    const result = await lastValueFrom(
      this.saveToleranceRisqueGQL.mutate({
        variables: { input },
      })
    );
    if (result.error) {
      throw result.error;
    }
    const qpiData = result.data?.saveToleranceRisque;
    return qpiData
      ? ({
          id: qpiData.id,
          investisseurId: qpiData.investisseurId,
          versionProfil: qpiData.versionProfil,
          resultatToleranceAuRisque: qpiData.resultatToleranceAuRisque ?? undefined,
          resultatProfilDeRisque: qpiData.resultatProfilDeRisque ?? undefined,
          reponsesToleranceRisque: qpiData.reponsesProfil?.toleranceRisque ?? null,
        } as QPIResults)
      : null;
  }

  /**
   * Display an existing QPI document
   */
  public async displayExistingQPI() {
    this.loadingService.startLoading();
    let qpiMetadata: StorageFile | undefined | null;
    try {
      // on récupere le QPI document ID dans l'avancement et on le wrap dans un StorageFile
      const qpiDocId = this.investisseur()?.avancementConnaissanceClientInvestisseur?.qpIdocumentID;
      if (qpiDocId) {
        const qpiFileMetadata = await this.documentService.getFileMetadatasById(qpiDocId);
        if (qpiFileMetadata) {
          qpiMetadata = {
            fileName: qpiFileMetadata.fileName ?? 'inconnu.pdf',
            documentType: DocumentTypeEnum.Qpi,
            fileExtension: 'pdf',
            id: qpiFileMetadata.fileId ?? '',
            fileType: 'application/pdf',
            fileConnectionInfo: {
              sasUrl: qpiFileMetadata?.url ?? '',
              blobFileId: qpiFileMetadata?.fileId ?? '',
              expiration: qpiFileMetadata?.urlExpireDate ?? undefined,
              permissions: [BlobFilesPermissionDs.Read],
            },
            isBlob: true,
          };
        }
      }
    } finally {
      this.loadingService.stopLoading();
    }
    if (qpiMetadata) {
      this.qpiViewerPanelService.openQPIDocumentViewer(qpiMetadata);
      return;
    }
  }
  /**
   * Generate QPI document
   */
  public async generateQPI(investisseurId: number | null = null, viewerOptions: DocumentViewerOpenOptions = {}) {
    this.loadingService.startLoading();
    let qpiMetadata: StorageFile | undefined | null;
    const currentInvestisseurId = investisseurId ?? this.investisseur()?.id;
    try {
      qpiMetadata = await this.getQPIMetadata(currentInvestisseurId);
    } finally {
      this.loadingService.stopLoading();
    }
    if (qpiMetadata) {
      this.qpiViewerPanelService.openQPIDocumentViewer(qpiMetadata, viewerOptions);
      return;
    }
  }
  public async loadUnsignedDocument(investisseurId: number, preLoadedMetadata?: StorageFile | null): Promise<FileDocument | null> {
    const fileMetadata = preLoadedMetadata ?? (await this.getQPIMetadata(investisseurId));
    if (isDevMode()) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    if (fileMetadata?.fileConnectionInfo?.sasUrl) {
      const url = await this.documentService.fetchAndCacheBlobDocument(fileMetadata.fileConnectionInfo.sasUrl, fileMetadata.fileType);
      if (url) {
        return new FileDocument(
          fileMetadata?.fileName as string,
          fileMetadata?.lastModificationDate,
          GedDocumentType.ProfInvest,
          url,
          this.qpiDocumentDescription,
          undefined,
          [investisseurId]
        );
      }
    }
    return null;
  }

  public async generateForOperation(investisseur: Investisseur): Promise<StorageFile> {
    const metadataId = investisseur.avancementConnaissanceClientInvestisseur?.metadatasConnaissanceClient?.id;
    if (!metadataId) {
      throw new Error('QPI metadata ID is not available for this investor.');
    }

    const document = await this.getQPIMetadata(investisseur.id, metadataId);
    if (!document?.fileConnectionInfo?.sasUrl) {
      throw new Error('Unable to generate QPI document for investor.');
    }
    return document;
  }

  public async loadSignedDocument(): Promise<FileDocument | null> {
    const documents = await firstValueFrom(
      this.investisseurService.investisseurDocuments$.pipe(filter((docs): docs is FileMetadata[] => docs !== null))
    );
    const signedFile = documents
      ?.filter(
        f =>
          f.typeDocumentCode === GedDocumentType.ProfInvest &&
          f.etatSignature === SignatureState.Completed &&
          getRawFileId(f.fileId) === getRawFileId(this.investisseur()?.avancementConnaissanceClientInvestisseur?.qpIdocumentID)
      )
      ?.sort((a, b) => new Date(b.dateDeDerniereModification || 0).getTime() - new Date(a.dateDeDerniereModification || 0).getTime())?.[0];
    if (signedFile) {
      return FileDocument.fromFileMetadata(signedFile, this.qpiDocumentDescription);
    }
    return null;
  }
  public getFakeDocument(): FileDocument {
    const document = new FileDocument(this.getFileName(), new Date(), GedDocumentType.ProfInvest, undefined, this.qpiDocumentDescription);
    document.isFakeFile = true;
    return document;
  }
  private async getQPIMetadata(investisseurId: number, metadataId?: number): Promise<StorageFile | null | undefined> {
    const qpiMetadataId = metadataId ?? this.investisseur()?.avancementConnaissanceClientInvestisseur?.metadatasConnaissanceClient?.id;
    if (!qpiMetadataId) {
      console.error('QPI metadata ID is not available.');
      return null;
    }
    // Call the service to generate the QPI document
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.GenerateQpiGQL.mutate({
        fetchPolicy: 'network-only',
        variables: {
          investisseurPrincipalId: investisseurId ?? this.investisseur()?.id,
        },
      })
    );
    if (result.error) {
      console.error('Error generating QPI:', result.error);
      this.snackbarService.open(result.error.message, 'error', 5000);
      return null;
    }
    return result.data!.generateQPI;
  }
  /**
   * Restart a QPI for an investor
   */
  async restartQPI(investisseurId: number): Promise<boolean> {
    const result = await lastValueFrom(
      this.restartInvestisseurQpiGQL.mutate({
        fetchPolicy: 'network-only',
        variables: { investisseurPrincipalId: investisseurId },
      })
    );
    if (result.error) {
      throw result.error;
    }
    await this.investisseurService.refreshRecueilState();
    return !!result.data?.restartInvestisseurQPI.id;
  }
  /**
   * Helper method to convert form controls to QPI criteria format
   */
  convertFormControlsToCriteres(formValue: any): QpiCritereInput[] {
    const criteres: QpiCritereInput[] = [];
    // Convert form controls to criteres format
    Object.keys(formValue).forEach(key => {
      const value = formValue[key];
      if (value !== null && value !== undefined && value !== '') {
        criteres.push({
          id: key,
          valeur: value.toString(),
        });
      }
    });
    return criteres;
  }
  private parseQpiCriteres(jsonValue?: string | null): Array<QpiCritereInput> | undefined {
    if (!jsonValue) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(jsonValue);
      if (Array.isArray(parsed)) {
        return parsed as Array<QpiCritereInput>;
      }
      // Legacy payloads can be double-encoded JSON strings.
      if (typeof parsed === 'string') {
        const parsedTwice = JSON.parse(parsed);
        if (Array.isArray(parsedTwice)) {
          return parsedTwice as Array<QpiCritereInput>;
        }
      }
    } catch {
      return undefined;
    }
    return undefined;
  }
  private getFileName(): string {
    const investisseur = this.investisseur() as Investisseur;
    return `Profil-Investisseur-${investisseur.personnePhysique[0].nom.trim()}_${investisseur.personnePhysique[0].prenom.trim()}-${this.datePipe.transform(new Date(), 'dd_MM_yyyy')}.pdf`.replace(
      ' ',
      ''
    );
  }
}
